import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { DateText } from '@/components/ui/DateText';
import {
  formatEur,
  settlementStatusLabel,
  settlementStatusStyle,
} from '@/lib/cashFlow';
import type { SettlementView } from '@/lib/settlementDetail';
import { SignedEur } from '../SignedEur';
import { SettlementRing } from './SettlementRing';

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 truncate text-right text-foreground/90">{v}</dd>
    </div>
  );
}

/** An amount that is tinted by direction in the register (signed) but a plain magnitude on
 *  the calendar (single-direction context). `cents` is already signed/unsigned in the view. */
function Amount({ cents, signed }: { cents: number; signed: boolean }) {
  return signed ? (
    <SignedEur cents={cents} />
  ) : (
    <span className="tabular-nums font-semibold text-foreground/90">{formatEur(cents)}</span>
  );
}

/**
 * The shared settlement read-out used by BOTH cash-flow lenses (L14 — one truth): a PIE
 * ring + progress bar of the paid share, the gross / paid / outstanding figures, the
 * payment count + last-paid date, and the direction-aware status badge. Every number comes
 * from the `SettlementView` (finance-core selections); this component does NO money maths
 * (L9). The register passes a `signed` view (income +, cost −); the calendar passes
 * magnitudes. "Tasutud" (ring, bar, figure) is tinted by DIRECTION — a received receivable
 * reads income-green, a paid payable the calmer settled-cyan — so laekunud and makstud never
 * look identical in either lens, while gross/outstanding follow the lens (`Amount`).
 */
export function SettlementDetail({ view }: { view: SettlementView }) {
  const kind = view.isIncome ? 'income' : 'cost';
  const pct = view.paidPct;
  const st = settlementStatusStyle(view.status, kind);
  // Direction tint shared by the ring, the bar and the "Tasutud" figure: income-green for a
  // receivable that laekub, the calmer settled-cyan for a payable that makstakse.
  const paidTint = kind === 'income' ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--settled))]';
  const paidBar = kind === 'income' ? 'bg-[hsl(var(--income))]' : 'bg-[hsl(var(--settled))]';
  // "Viimati makstud": the event-sourced last-payment date, falling back to the legacy
  // mark-paid (`actualDate`) so a pre-events settled row still shows when it cleared.
  const settledOn = view.lastPaidOn ?? view.actualDate;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2.5">
        <SettlementRing pct={pct} status={view.status} kind={kind} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
              st.badge,
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', st.dot)} aria-hidden />
            {settlementStatusLabel(view.status, kind)}
          </span>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-[hsl(var(--border))]"
            role="presentation"
          >
            <div
              className={cn('h-full rounded-full', paidBar)}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
        </div>
      </div>
      <dl className="space-y-0.5 border-t border-border/60 pt-1">
        <Row k="Summa" v={<Amount cents={view.gross} signed={view.signed} />} />
        <div className="flex justify-between gap-3">
          <dt className="shrink-0 text-muted-foreground">Tasutud</dt>
          <dd className={cn('tabular-nums', paidTint)}>
            {formatEur(view.paid)}
            <span className="ml-1 text-muted-foreground">{pct}%</span>
          </dd>
        </div>
        <Row k="Jääk" v={<Amount cents={view.outstanding} signed={view.signed} />} />
        {view.paymentCount > 0 && (
          <Row k="Maksed" v={<span className="tabular-nums">{view.paymentCount}</span>} />
        )}
        {settledOn && (
          <Row k="Viimati makstud" v={<DateText iso={settledOn} className="tabular-nums" />} />
        )}
        <Row k="Tähtaeg" v={<DateText iso={view.expectedDate} className="tabular-nums" />} />
        {view.overdueAge !== null && view.overdueAge.days > 0 && (
          <Row
            k="Üle tähtaja"
            v={
              <span className="tabular-nums text-[hsl(var(--cost))]">
                <span className="font-semibold">{view.overdueAge.days} p</span>
                {' · alates '}
                <DateText iso={view.overdueAge.since} />
              </span>
            }
          />
        )}
      </dl>
    </div>
  );
}
